import React, { useEffect, useRef } from 'react';
import { Icon, Spin, Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import routes from 'src/routes';
import { useAppStateContext } from 'src/stateProviders/useAppStateContext';
import AlertSegment from 'src/components/ProductDetails/types/AlertSegment';
import InsightSegment from 'src/components/ProductDetails/types/InsightSegment';
import useAlertListFetch from 'src/components/ProductDetails/hooks/useAlertListFetch';
import useInsightListFetch from 'src/components/ProductDetails/hooks/useInsightListFetch';
import ErrorAlert from 'src/components/common/ErrorAlert';
import styles from './AlertsDetails.module.less';

const antIcon = <Icon type="loading" spin />;

type AlertsDetailsProps = {
  emailId: string;
  ownerId: string;
  matchType: string;
  priceType: string;
  region: string;
  sourceOwnerId: string;
  sourceProductId: string;
};

type AlertWidgetProps = {
  data: AlertSegment[];
};

type InsightWidgetProps = {
  data: InsightSegment[];
};

const AlertWidget: React.FC<AlertWidgetProps> = ({ data }) => {
  return (
    <>
      {data.map(({ alertId, alertName, uniqueId }, index) => (
        <div className={styles.alert} key={index}>
          <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} size={'sm'} className={styles.alertIcon} />
          <Link to={`${routes.alertResults}/${alertId}?uniqueId=${uniqueId}`} className={styles.link} target="_blank">
            {alertName}
          </Link>
        </div>
      ))}
    </>
  );
};

const InsightWidget: React.FC<InsightWidgetProps> = ({ data }) => {
  return (
    <>
      <div className={styles.insight}>
        <ul>
          {data
            .filter(({ ownerId }) => ownerId === 0)
            .map(({ insightMessages }) =>
              insightMessages.map((insight, i) => (
                <li key={i}>
                  <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} size={'sm'} className={styles.insightIcon} />
                  <span className={styles.ownerSite}>{insight}</span>
                </li>
              ))
            )}
        </ul>
        <ul>
          {data
            .filter(({ ownerId }) => ownerId !== 0)
            .map(({ insightMessages }) =>
              insightMessages.map((insight, i) => (
                <li key={i}>
                  <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} size={'sm'} className={styles.insightIcon} />
                  {insight}
                </li>
              ))
            )}
        </ul>
      </div>
    </>
  );
};

const AlertsDetails: React.FC<AlertsDetailsProps> = ({
  emailId,
  matchType,
  ownerId,
  region,
  priceType,
  sourceOwnerId,
  sourceProductId,
}) => {
  const [{ loading: alertLoading, data: alertData, error: alertError }] = useAlertListFetch({
    emailId,
    matchType,
    regionId: region,
    sourceProductId,
  });
  const [{ loading: insightLoading, data: insightData, error: insightError }] = useInsightListFetch({
    matchType,
    ownerId,
    regionId: region,
    priceType,
    sourceOwnerId,
    sourceProductId,
  });
  const { user } = useAppStateContext();
  const alerts = alertData || [];
  const insights = insightData || [];
  const hasInsights = user?.appPermissions['enable_insight'];
  const hasAlerts = user?.appPermissions['enable_alert'];
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.style.height = 'auto';
    }
  }, [alerts, insights, alertLoading, insightLoading]);

  const isDataAvailable = alerts.length > 0 || insights.length > 0;

  return (
    <div>
      {hasInsights && hasAlerts && (
        <div className={styles.header}>
          <Tooltip
            title={
              <p>
                This section shows the alerts for the product, in below format:
                <br />
                <br /> Alertname - Region
              </p>
            }
          >
            <Icon type="info-circle" className={styles.topRightIcon} />
          </Tooltip>
        </div>
      )}
      {!hasInsights && hasAlerts && (
        <div className={styles.header}>
          <Tooltip
            title={
              <p>
                This section shows the alerts for the product, in below format:
                <br />
                <br /> Alertname - Region
              </p>
            }
          >
            <Icon type="info-circle" className={styles.topRightIcon} />
          </Tooltip>
        </div>
      )}
      <ErrorAlert isVisible={alertError} errorLabel={'alerts'} />
      <ErrorAlert isVisible={insightError} errorLabel={'insights'} />
      <div className={styles.container} ref={containerRef}>
        <Spin indicator={antIcon} size={'large'} spinning={alertLoading || insightLoading}>
          {isDataAvailable ? (
            <>
              {hasInsights && <InsightWidget data={insights} />}
              {hasAlerts && <AlertWidget data={alerts} />}
            </>
          ) : (
            <p className={styles.noResults}>No Insights or Alerts data currently available.</p>
          )}
        </Spin>
      </div>
    </div>
  );
};

export default AlertsDetails;
